import React, { useEffect, useRef, useState } from 'react';

import { Plan, VenueDisposition, VenueDispositionType } from '@go-shared/types/plan';
import { VenueTemplate } from '@go-shared/types/venueTemplate';
import { Event } from '@go-shared/types/event';
import { timeToMinutes, DAY_LENGTH } from '@go-shared/lib/time';
import { Time } from '@go-shared/types/time';
import { GridBody } from '.';
import { getBlocksInRange, blockToTime } from '@go-shared/lib/grid';
import { GroupEvent } from '@go-shared/types/groupEvent';
import {
  VenueDataStyle,
  GroupEventElement,
  EventElement,
  VenueElement,
  ReserveEventElement,
} from './GridBody';
import { GridEventPos, GridEventStyle } from './GridEvent';

import './Grid.scss';
import { PropertyType } from '@go-shared/types/property';
import { BubbleButton } from './BubbleUI';
import { groupBy, lastEl, mapBy } from '@go-shared/lib/functional';
import uuid from 'uuid';
import { BrandMealPeriod } from '@go-shared/lib/mealPeriods';
import { getHourFormat } from '@go-shared/lib/property';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { unsafeDeepCopy } from '@go-shared/lib/utils';
import { getCommonTexts } from '@go-shared/lib/brand';
import { HourFormat } from '@go-shared/types/brand';
import { ReserveEvent } from '@go-shared/types/reserve';
import { EventTemplate } from '@go-shared/types/eventTemplate';
import { StaffTemplate } from '@go-shared/types/staffTemplate';
import { BareContentTemplate } from '@go-shared/types/contentTemplate';
import { populateEvent } from '@go-shared/lib/event';

// Necessary data to calculate the event position and size
interface GridEventData {
  _id: string;
  duration: number;
  startBlock: number;
  durationBlocks: number;
  endBlock: number;
  track: number;
  style?: GridEventStyle;
  setUpTimeStart?: number;
  breakdownTimeEnd?: number;
}

export interface GridLocationData {
  venue: VenueTemplate;
  time: Time;
  position: { x: number; y: number };
}

export interface GridProps {
  departureTimes?: Time[];
  arrivalTimes?: Time[];
  variant: 'navy' | 'velvet';
  singleColMode: boolean;
  plan: Plan;
  propertyType: PropertyType;
  events: Event[];
  reserveEvents: ReserveEvent[];
  levelLabel: string;
  groupEvents: GroupEvent[];
  title?: string;
  dayStart?: Time;
  venues: VenueTemplate[];
  venueLocked: boolean;
  eventsLocked: boolean;
  cellWidth: number;
  cellHeight: number;
  eventTemplates: EventTemplate[];
  venueTemplates: VenueTemplate[];
  staffTemplates: StaffTemplate[];
  contentTemplates: BareContentTemplate[];
  afterGridSlot?: React.ReactNode;
  gridRightMargin?: number;
  gridLeftMargin?: number;
  onScroll?: (event: React.UIEvent<HTMLDivElement>) => void;
  onVenueClick?: (venue: VenueTemplate) => void;
  onEventDoubleClick?: (event: Event) => void;
  onEventDelete?: (eventId: string) => void;
  onLocationDoubleClick?: (locationData: GridLocationData) => void;
  onEventMove?: (newEvent: Event, oldEvent: Event) => void;
  onEventResize?: (newEvent: Event, oldEvent: Event) => void;
  onToggleEventHiddenClick?: (event: Event) => void;
  onStaffAllocationEditClick?: (event: Event) => void;
  onChangeCellWidth?: (amt: number) => void;
  onChangeCellHeight?: (amt: number) => void;
  onUpdateEvent?: (event: Event) => void;
  onCreateEventFromReserve?: (event: Event) => void;
}

export interface GridState {
  scrollPosition: GridScrollPosition;
  venueDisplayType: VenueDispositionType;
  gridHeight: number;
  dayWidth: number;
  gridBodyWidth: number;
  eventElements: EventElement[];
  groupEventElements: GroupEventElement[];
  dispositionElements: VenueDataStyle[];
  venueElements: VenueElement[];
}

export interface GridScrollPosition {
  scrollTop: number;
  scrollLeft: number;
}

const HEADER_HEIGHT = 60;
const HEADER_WIDTH = 75;

export const BLOCK_LENGTH = 15; // In minutes
const NUM_BLOCKS = 24 * (60 / BLOCK_LENGTH);
const MINIMUM_EVENT_DURATION = BLOCK_LENGTH; // In minutes

const EVENT_GAP = 8; // in pixels

export const CELL_HEIGHT_STEP = 1;
export const DEFAULT_CELL_HEIGHT = 30;
export const MIN_CELL_HEIGHT = 1;
export const MAX_CELL_HEIGHT = 180;

export const CELL_WIDTH_STEP = 1;
export const DEFAULT_CELL_WIDTH = 120;
export const MIN_CELL_WIDTH = 1;
export const MAX_CELL_WIDTH = 1000;

// Time which will stick to the top of the canvas
const INITIAL_DAILY_PLANNER_TIME: Time = { hour: 10, minute: 0 };

export default function Grid(props: GridProps) {
  const serverInfo = useServerInfo();
  const commonTexts = getCommonTexts(
    {
      default: serverInfo.brand.commonTexts,
      translations: [],
    },
    { selected: 'en' }
  );

  const gridBodyRef = useRef<HTMLDivElement>(null);

  const [scrollPosition, setScrollPosition] = useState<GridScrollPosition>({
    scrollTop: 0,
    scrollLeft: 0,
  });
  const [isGridBusy, setIsGridBusy] = useState(false);
  const [forceVisualUpdate, setForceVisualUpdate] = useState<Event | undefined>(undefined);
  const [venueDisplayType, setVenueDisplayType] = useState<VenueDispositionType>('openCloseHours');
  const [allElements, setAllElements] = useState<{
    eventEls: EventElement[];
    reserveEventEls: EventElement[];
    groupEventEls: GroupEventElement[];
    venueEls: VenueElement[];
    dispositionEls: VenueDataStyle[];
  }>({ eventEls: [], reserveEventEls: [], groupEventEls: [], venueEls: [], dispositionEls: [] });
  const hourFormat = getHourFormat(props.plan.property);

  const blocks = getBlocksInRange(
    0,
    DAY_LENGTH,
    BLOCK_LENGTH,
    timeToMinutes(props.plan.dayStart),
    hourFormat
  );
  const dayStart = props.plan.dayStart;
  const dayStartMinutes = timeToMinutes(dayStart);
  const gridHeight = NUM_BLOCKS * props.cellHeight;
  const mealPeriods: BrandMealPeriod[] = serverInfo.brand.mealPeriods;

  useEffect(() => {
    goToTime(INITIAL_DAILY_PLANNER_TIME);
  }, [gridBodyRef]);

  useEffect(() => {
    if (isGridBusy) return; // prevent canvas updates while grid is busy

    const [evtEls, reserveEvtEls, groupEvtEls, dispoEls, venEls] = calculateElements(
      props.venues,
      props.plan.venueData,
      props.events,
      props.reserveEvents.map(re =>
        populateEvent(
          re,
          props.eventTemplates,
          props.venueTemplates,
          props.staffTemplates,
          props.plan.day
        )
      ),
      props.groupEvents,
      mealPeriods,
      hourFormat,
      dayStartMinutes,
      props.cellWidth,
      props.cellHeight,
      forceVisualUpdate,
      props.singleColMode
    );

    setAllElements({
      eventEls: evtEls,
      reserveEventEls: reserveEvtEls,
      groupEventEls: groupEvtEls,
      venueEls: venEls,
      dispositionEls: dispoEls,
    });

    if (props.singleColMode) {
      setVenueDisplayType('openCloseHours');
    }
  }, [
    props.venues,
    props.events,
    props.groupEvents,
    props.plan,
    props.cellWidth,
    props.cellHeight,
    props.singleColMode,
  ]);

  const { eventEls, reserveEventEls, groupEventEls, venueEls, dispositionEls } = allElements;
  const dayWidth = calculateDayWidth(venueEls, props.cellWidth);

  // use this to convert an x position in the grid to a venue (accounts for venues spanning)
  const trackToVenElMap = makeTrackToVenElMap(venueEls);

  function goToTime(time: Time) {
    if (props.dayStart) {
      const minutes = timeToMinutes(time, timeToMinutes(props.dayStart));
      gridBodyRef.current?.scroll({ top: (minutes / BLOCK_LENGTH) * props.cellHeight, left: 0 });
    }
  }

  function handleScroll(event: React.UIEvent<HTMLDivElement>) {
    const { scrollTop, scrollLeft } = event.currentTarget;
    setScrollPosition({ scrollTop, scrollLeft });
    if (props.onScroll) props.onScroll(event);
  }

  // we keep track of when the user is dragging an event so that we can prohibit
  // updating the canvas while it is busy, and instead wait until the user is done
  // dragging before doing the update.
  function handleBusy(busy: boolean) {
    setIsGridBusy(busy);
  }

  function handleDoubleClick(event: React.MouseEvent<HTMLDivElement>) {
    const bb = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - bb.left;
    const y = event.clientY - bb.top;

    if (props.onLocationDoubleClick) {
      props.onLocationDoubleClick(getDataAt(x, y));
    }
  }

  function handleEventMove(event: Event, to: GridEventPos) {
    const newLocation = getDataAt(to.x, to.y, true);
    const newEvent = {
      ...event,
      day: props.plan.day,
      venue: newLocation.venue,
      startTime: newLocation.time,
    };

    setForceVisualUpdate(event);
    if (props.onEventMove) props.onEventMove(newEvent, event);
  }

  function handleEventResize(event: Event, height: number) {
    const duration = Math.max(
      MINIMUM_EVENT_DURATION,
      Math.round(height / props.cellHeight) * BLOCK_LENGTH
    );

    const newEvent = { ...event, duration };

    setForceVisualUpdate(event);
    if (props.onEventResize) props.onEventResize(newEvent, event);
  }

  function getDataAt(x: number, y: number, snap?: boolean): GridLocationData {
    // Adjust if dragging to the left or top of the canvas
    if (x < 0) x = 0;
    if (y < 0) y = 0;

    if (snap) {
      x += Math.floor(props.cellWidth / 2);
      y += Math.floor(props.cellHeight / 2);
    }

    // Adjust if dragging to the right of the canvas (No need to adjust for
    // events that are under the canvas, because we don't leave space there)
    if (x >= dayWidth) {
      x = dayWidth - props.cellWidth;
    }

    const trackIndex = Math.floor(x / props.cellWidth);
    const blockIndex = Math.floor(y / props.cellHeight);
    const venueEl = trackToVenElMap[trackIndex];

    // This is the actual position of the cell where the elemnent is falling
    const position = {
      x: trackIndex * props.cellWidth,
      y: blockIndex * props.cellHeight,
    };

    const timeBlock = blocks[blockIndex];
    const time = blockToTime(timeBlock.index, BLOCK_LENGTH, timeToMinutes(props.plan.dayStart));

    return {
      venue: venueEl.venue,
      time,
      position,
    };
  }

  const left = `${props.gridLeftMargin}px`;
  const width = `calc(100% - ${props.gridRightMargin}px - ${props.gridLeftMargin}px)`;

  return (
    <div
      className={`Grid relative h-full bg-white ${props.variant} ${
        props.venueLocked ? 'v-locked' : ''
      } ${props.eventsLocked ? 'e-locked' : ''}`}
      style={{ left, width }}
    >
      <div
        className="GridHeaderH flex absolute"
        style={{ width: dayWidth, left: HEADER_WIDTH - scrollPosition.scrollLeft }}
      >
        <div key={props.plan.day._id} className="GridDay p-0 m-0" style={{ width: dayWidth }}>
          <div className="flex">
            <div className="GridDayHeader GridCell p-0 m-0 text-white" style={{ width: dayWidth }}>
              {props.title}
            </div>
          </div>
          <div className="flex">
            {venueEls.map((ve, j) => (
              <div key={j} className="GridVenueCell GridCell text-center" style={ve.style}>
                {!props.venueLocked && !props.singleColMode ? (
                  <a
                    className="GridVenueCellContents"
                    onClick={props.onVenueClick ? () => props.onVenueClick!(ve.venue) : undefined}
                    title={`${ve.venue.name}, Deck ${ve.venue.level}`}
                  >
                    {ve.venue.name}
                  </a>
                ) : (
                  <span className="GridVenueCellContents">{ve.venue.name}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="GridHeaderV" style={{ top: HEADER_HEIGHT - scrollPosition.scrollTop }}>
        {blocks.map((block, i) => (
          <div
            key={i}
            className="GridHeaderBlocksCell"
            style={{ height: props.cellHeight, lineHeight: props.cellHeight + 'px' }}
          >
            {block.name}
          </div>
        ))}
      </div>
      <div className="GridCorner grid justify-center items-center">
        <BubbleButton
          variant="white"
          inverse
          className="stretched-link"
          disabled={props.singleColMode}
          onClick={() => {
            setVenueDisplayType(
              venueDisplayType === 'openCloseHours' ? 'mealPeriods' : 'openCloseHours'
            );
          }}
          icon={venueDisplayType === 'openCloseHours' ? 'circle-half-stroke' : 'utensils'}
        />
      </div>
      <div
        onScroll={handleScroll}
        className={`GridBodyWrapper ${props.eventsLocked ? 'locked' : ''}`}
        ref={gridBodyRef}
      >
        <GridBody
          commonTexts={commonTexts}
          mcmEndpoints={serverInfo.mcmEndpoints}
          audiences={props.plan.property.zones.map(z => z._id)}
          eventEls={eventEls}
          reserveEventEls={reserveEventEls}
          groupEventEls={groupEventEls}
          venueEls={venueEls}
          venueData={dispositionEls}
          width={dayWidth}
          height={gridHeight}
          locked={props.eventsLocked}
          hourFormat={hourFormat}
          levelLabel={props.levelLabel}
          cellWidth={props.cellWidth}
          cellHeight={props.cellHeight}
          departureTimes={props.departureTimes}
          arrivalTimes={props.arrivalTimes}
          propertyType={props.propertyType}
          dayStart={props.dayStart}
          venueDisplayType={venueDisplayType}
          scrollPosition={scrollPosition}
          mealPeriods={mealPeriods}
          onDoubleClick={handleDoubleClick}
          onEventDelete={props.onEventDelete}
          onEventMove={handleEventMove}
          onEventResize={handleEventResize}
          onEventEdit={props.onEventDoubleClick}
          onToggleEventHiddenClick={props.onToggleEventHiddenClick}
          onStaffAllocationEditClick={props.onStaffAllocationEditClick}
          onIsBusy={handleBusy}
          onUpdateEvent={props.onUpdateEvent}
          onCreateEventFromReserve={props.onCreateEventFromReserve}
        />
        {props.afterGridSlot}
      </div>
    </div>
  );
}

function calculateElements(
  venues: VenueTemplate[],
  dispositions: VenueDisposition[],
  events: Event[],
  reserveEvents: Event[],
  groupEvents: GroupEvent[],
  allMealPeriods: BrandMealPeriod[],
  hourFormat: HourFormat,
  dayStartMinutes: number,
  cellWidth: number,
  cellHeight: number,
  forceVisualUpdate: Event | undefined,
  singleColMode: boolean
): [EventElement[], EventElement[], GroupEventElement[], VenueDataStyle[], VenueElement[]] {
  const venuesMap = mapBy(venues, '_id');
  const eventsByVen = groupBy(events, e => e.venue._id);
  const reserveEventsByVen = groupBy(reserveEvents, e => e.venue._id);
  const groupEventsByVen = groupBy(groupEvents, e => e.venue._id);
  const dispositionsByVen = mapBy(dispositions, d => d.template);

  let allEventEls: EventElement[] = [];
  let allReserveEventEls: EventElement[] = [];
  let allGroupEventEls: GroupEventElement[] = [];
  let allDispositionEls: VenueDataStyle[] = [];

  // cursor that keeps track of what column the last venue left off on
  // (each venues may take up more than one column) this is used as a left
  // offset for event elements calculated for the next venue
  let trackStart = 0;

  let venueEls = venues.map((ven, i) => {
    const [eventEls, reserveEventEls, groupEventEls, tracks] = calculateEventElements(
      singleColMode ? events.filter(e => venuesMap[e.venue._id]) : eventsByVen[ven._id] || [],
      singleColMode
        ? reserveEvents.filter(e => venuesMap[e.venue._id])
        : reserveEventsByVen[ven._id] || [],
      singleColMode
        ? groupEvents.filter(g => venuesMap[g.venue._id])
        : groupEventsByVen[ven._id] || [],
      hourFormat,
      dayStartMinutes,
      trackStart,
      cellWidth,
      cellHeight,
      forceVisualUpdate
    );
    const dispositionEls = calculateDispositionElements(
      dispositionsByVen[ven._id],
      allMealPeriods,
      dayStartMinutes,
      trackStart,
      tracks,
      cellWidth,
      cellHeight
    );
    const venueEl: VenueElement = {
      venue: ven,
      style: {
        width: Math.max(tracks * cellWidth, 0),
        maxWidth: Math.max(tracks * cellWidth, 0),
      },
      eventEls,
      reserveEventEls,
      groupEventEls,
      dispositionEls,
      meta: { trackStart, trackSize: tracks },
    };

    trackStart += tracks;

    if (!singleColMode || i === 0) {
      allEventEls = allEventEls.concat(eventEls);
      allReserveEventEls = allReserveEventEls.concat(reserveEventEls);
      allGroupEventEls = allGroupEventEls.concat(groupEventEls);
      allDispositionEls = allDispositionEls.concat(dispositionEls);
    }

    return venueEl;
  });

  if (singleColMode && venueEls.length > 0) {
    const firstVen = unsafeDeepCopy(venueEls[0]);

    for (let i = 1; i < venueEls.length; i++) {
      firstVen.dispositionEls = [];
      firstVen.eventEls = firstVen.eventEls.concat(venueEls[i].eventEls);
      firstVen.reserveEventEls = firstVen.reserveEventEls.concat(venueEls[i].reserveEventEls);
      firstVen.groupEventEls = firstVen.groupEventEls.concat(venueEls[i].groupEventEls);
    }

    firstVen.venue.name = 'Venues';

    venueEls = [firstVen];
  }

  return [allEventEls, allReserveEventEls, allGroupEventEls, allDispositionEls, venueEls];
}

function calculateDayWidth(venueEls: VenueElement[], cellWidth: number) {
  const lastVenEl = lastEl(venueEls);
  return lastVenEl ? cellWidth * (lastVenEl.meta.trackStart + lastVenEl.meta.trackSize) : 0;
}

/**
 * Calculates events and groupEvents' position based on which venue they happen in, and whether
 * they conflict with other events
 */
function calculateEventElements(
  events: Event[],
  reserveEvents: Event[],
  groupEvents: GroupEvent[],
  hourFormat: HourFormat,
  dayStartMinutes: number,
  trackStart: number,
  cellWidth: number,
  cellHeight: number,
  forceVisualUpdate: Event | undefined
): [EventElement[], EventElement[], GroupEventElement[], number] {
  const eventMap = mapBy(events, '_id');
  const reserveEventMap = mapBy(reserveEvents, '_id');
  const groupEventMap = mapBy(groupEvents, '_id');

  // combine to process together
  let eventData: GridEventData[] = [];

  events.forEach(evt => {
    const startBlock = timeToMinutes(evt.startTime, dayStartMinutes) / BLOCK_LENGTH;
    const durationBlocks = Math.max(Math.floor(evt.duration / BLOCK_LENGTH), 1);
    const endBlock = startBlock + durationBlocks;

    eventData.push({
      _id: evt._id,
      duration: evt.duration,
      startBlock,
      durationBlocks,
      endBlock,
      track: 1,
    });
  });

  reserveEvents.forEach(evt => {
    const startBlock = timeToMinutes(evt.startTime, dayStartMinutes) / BLOCK_LENGTH;
    const durationBlocks = Math.max(Math.floor(evt.duration / BLOCK_LENGTH), 1);
    const endBlock = startBlock + durationBlocks;

    eventData.push({
      _id: evt._id,
      duration: evt.duration,
      startBlock,
      durationBlocks,
      endBlock,
      track: 1,
    });
  });

  groupEvents.forEach(gevt => {
    const startBlock = timeToMinutes(gevt.startTime, dayStartMinutes) / BLOCK_LENGTH;
    const durationBlocks = Math.max(Math.floor(gevt.duration / BLOCK_LENGTH), 1);
    const endBlock = startBlock + durationBlocks;
    const setUpTimeStart = Math.max(gevt.bufferTime.setUpTime / BLOCK_LENGTH, 0) * cellHeight;
    const breakdownTimeEnd = Math.max(gevt.bufferTime.breakdownTime / BLOCK_LENGTH, 0) * cellHeight;

    eventData.push({
      _id: gevt._id,
      duration: gevt.duration,
      startBlock,
      durationBlocks,
      endBlock,
      track: 1,
      setUpTimeStart,
      breakdownTimeEnd,
    });
  });

  eventData.sort((a, b) => b.duration - a.duration);

  const venueStartPos = trackStart * cellWidth;

  // seperate events into tracks
  const tracks: GridEventData[][] = [];

  eventData.forEach(ed => {
    let slotted = false;

    for (let track of tracks) {
      if (!trackIntersect(ed, track)) {
        track.push(ed);
        slotted = true;
        break;
      }
    }

    if (!slotted) {
      tracks.push([ed]);
    }
  });

  // split events back
  const evtElements: EventElement[] = [];
  const reserveEvtElements: ReserveEventElement[] = [];
  const groupEvtElements: GroupEventElement[] = [];

  // the width that each event will take up
  const eventWidth = cellWidth - EVENT_GAP / tracks.length;

  tracks.forEach((track, ti) => {
    track.forEach(ed => {
      let reach = 0;

      for (let i = ti + 1; i < tracks.length; i++) {
        if (!trackIntersect(ed, tracks[i])) reach++;
        else break;
      }

      const style: GridEventStyle = {
        left: venueStartPos + eventWidth * ti,
        top: ed.startBlock * cellHeight,
        width: Math.max(eventWidth * (1 + reach), 0),
        height: Math.max(ed.durationBlocks * cellHeight, 0),
      };

      if (eventMap[ed._id]) {
        // it is an event element
        evtElements.push({
          key: forceVisualUpdate && ed._id === forceVisualUpdate._id ? uuid() : ed._id,
          event: eventMap[ed._id],
          style,
          meta: {
            track: ti + 1,
            hourFormat,
          },
        });
      } else if (reserveEventMap[ed._id]) {
        // it is a reserve event element
        reserveEvtElements.push({
          key: uuid(),
          event: reserveEventMap[ed._id],
          style,
          meta: {
            track: ti + 1,
            hourFormat,
          },
        });
      } else if (groupEventMap[ed._id]) {
        // it is a group event element
        groupEvtElements.push({
          key: uuid(),
          groupEvent: groupEventMap[ed._id],
          style,
          meta: {
            track: ti + 1,
            hourFormat,
          },
          setUpTimeStart: ed.setUpTimeStart,
          breakdownTimeEnd: ed.breakdownTimeEnd,
        });
      }
    });
  });

  return [evtElements, reserveEvtElements, groupEvtElements, Math.max(tracks.length, 1)];
}

function calculateDispositionElements(
  disposition: VenueDisposition | undefined,
  allMealPeriods: BrandMealPeriod[],
  dayStartMinutes: number,
  trackStart: number,
  tracks: number,
  cellWidth: number,
  cellHeight: number
) {
  return [
    ...(disposition?.hours ?? []).map<VenueDataStyle>(h => {
      let startBlock = timeToMinutes(h.startTime, dayStartMinutes) / BLOCK_LENGTH;
      let endBlock = timeToMinutes(h.endTime, dayStartMinutes) / BLOCK_LENGTH;
      // Adjust the endblock if venue hours last till the end of day
      if (endBlock === 0) {
        endBlock = DAY_LENGTH / BLOCK_LENGTH;
      }
      return {
        type: 'openCloseHours',
        left: trackStart * cellWidth,
        top: startBlock * cellHeight,
        width: Math.max(tracks * cellWidth, 0),
        height: Math.max((endBlock - startBlock) * cellHeight, 0),
        color: h.status === 'closed' ? '#3c4650b3' : '#9bc8e680',
      };
    }),
    ...(disposition?.mealPeriods ?? []).map<VenueDataStyle>(h => {
      let startBlock = timeToMinutes(h.startTime, dayStartMinutes) / BLOCK_LENGTH;
      let endBlock = timeToMinutes(h.endTime, dayStartMinutes) / BLOCK_LENGTH;
      // Adjust the endblock if venue hours last till the end of day
      if (endBlock === 0) {
        endBlock = DAY_LENGTH / BLOCK_LENGTH;
      }
      const mealPeriodColor = allMealPeriods.find(el => el.code === h.code);
      return {
        type: 'mealPeriods',
        left: trackStart * cellWidth,
        top: startBlock * cellHeight,
        width: Math.max(tracks * cellWidth, 0),
        height: Math.max((endBlock - startBlock) * cellHeight, 0),
        color: mealPeriodColor ? mealPeriodColor.color : 'orange',
      };
    }),
  ];
}

function makeTrackToVenElMap(venueBlocks: VenueElement[]) {
  const trackToVenElMap: { [key: number]: VenueElement } = {};

  let trackIdx = 0;
  venueBlocks.forEach(vb => {
    let { trackSize } = vb.meta;

    while (trackSize > 0) {
      trackToVenElMap[trackIdx] = vb;
      trackIdx += 1;
      trackSize -= 1;
    }
  });

  return trackToVenElMap;
}

function trackIntersect(event: GridEventData, track: GridEventData[]) {
  // as soon as it intersects cut it off
  for (let e2 of track) {
    if (!(event.endBlock <= e2.startBlock || event.startBlock >= e2.endBlock)) {
      return true;
    }
  }

  return false;
}
